/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Tooltip,
  Table,
  Thead,
  Tr,
  Th,
  Td,
  Tbody,
  Text,
  Skeleton,
  Flex,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import { useQuery } from 'react-query'
import { FiEdit } from 'react-icons/fi'
import { AiFillDelete } from 'react-icons/ai'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  BlockTimesData,
  getBlockTimes
} from '../../hooks/blockTimes/useBlockTimes'
import { useAuth } from '../../hooks/auth'
import { LayoutList } from '../../layouts/List'
import { NotFound } from '../../components/molecules/NotFound'
import { Pagination } from '../../components/PaginationNew'
import { ModalConfirmExclude } from '../../components/Modal/confirmExclude'
import { apiAuth } from '../../services/apiAuth'
import { formatDateTime } from '../../utils/timezone'

export function BlockTimes() {
  const { decodeToken } = useAuth()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [t] = useTranslation('pageBlockTimes')
  const skeletonRepeat = [1, 2, 3, 4, 5, 6, 7]
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [idExclude, setIdExclude] = useState(0)
  const [search, setSearch] = useState('')

  const clinicTimezone = decodeToken?.clinic?.timezone || 'America/Sao_Paulo'

  const {
    data: getBlockTimesData,
    isFetching,
    refetch,
    isLoading
  } = useQuery(['blockTimes', currentPage, '', '', search], () =>
    getBlockTimes(currentPage, '', '', search)
  )

  useEffect(() => {
    refetch()

    return () => {}
  }, [])

  const SearchInputCallbackValue = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget

      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }

      searchTimerRef.current = setTimeout(() => {
        setSearch(value)
        setCurrentPage(1)
      }, 750)
    },
    []
  )

  const ActiveCheckboxCallbackValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {},
    []
  )

  async function handleExcludedBlockById(id: number) {
    const { status } = await apiAuth.delete(`clinics/block-times/${id}/`)

    if (status === 204) {
      toast({
        position: 'top-right',
        status: 'success',
        duration: 3000,
        title: 'Documento excluído',
        isClosable: true,
        description: 'Bloqueio excluído com sucesso'
      })
    } else {
      toast({
        position: 'top-right',
        status: 'warning',
        duration: 3000,
        title: 'Ação não realizada!',
        isClosable: true,
        description: 'Não foi possível excluir bloqueio'
      })
    }
  }

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!isOpen && idExclude) {
      refetch()
    }
  }, [idExclude, isOpen, refetch])

  return (
    <>
      <LayoutList
        title={t('title')}
        urlNew="/settings/block-times/create"
        refetch={refetch}
        isFetching={isFetching}
        capturaChange={(event: any) => SearchInputCallbackValue(event)}
        captureActiveChange={(event: any) => ActiveCheckboxCallbackValue(event)}
        placeholder={t('search')}
        isViewActive={false}
      >
        <Box>
          <Table variant="striped" colorScheme="blue" size="sm">
            <Thead>
              <Tr>
                <Th>{t('t1')}</Th>
                <Th>{t('t2')}</Th>
                <Th>{t('t3')}</Th>
                <Th>{t('t4')}</Th>
                <Th>{t('t5')}</Th>
                <Th>{t('t6')}</Th>
                <Th>{t('t7')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <>
                  {skeletonRepeat.map((skeleton) => (
                    <Tr key={skeleton}>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                      <Td>
                        <Skeleton height="20px" />
                      </Td>
                    </Tr>
                  ))}
                </>
              ) : (
                <>
                  {getBlockTimesData?.times.map(
                    (block: BlockTimesData, index: number) => (
                      <Tr key={block?.id || index} maxHeight="40px">
                        <Td>{block?.name}</Td>
                        <Td>
                          {block?.start_time ? (
                            <Text>
                              {formatDateTime(block.start_time, clinicTimezone)}
                            </Text>
                          ) : (
                            <Text>
                              {block?.recurrent_start_time?.slice(0, 5) || '-'}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {block?.finish_time ? (
                            <Text>
                              {formatDateTime(
                                block.finish_time,
                                clinicTimezone
                              )}
                            </Text>
                          ) : (
                            <Text>
                              {block?.recurrent_finish_time?.slice(0, 5) || '-'}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {block?.specialists?.map((specialist) => (
                            <Tooltip
                              label={specialist?.name}
                              aria-label={specialist?.name}
                              key={specialist?.id}
                            >
                              <Box>
                                <Text>{specialist?.social_name}</Text>
                              </Box>
                            </Tooltip>
                          ))}
                        </Td>
                        <Td>
                          {block?.description !== null && (
                            <Tooltip
                              label={block?.description || ''}
                              aria-label={block?.description || ''}
                              hasArrow
                              placement="top-start"
                            >
                              <Box ml="1rem" w="24px">
                                <Text noOfLines={1}>
                                  {block?.description || ''}
                                </Text>
                              </Box>
                            </Tooltip>
                          )}
                        </Td>
                        <Td>
                          <Text>
                            {block?.created_at
                              ? formatDateTime(block.created_at, clinicTimezone)
                              : 'Recorrente'}
                          </Text>
                        </Td>
                        <Td align="center" justifyContent="center">
                          <Flex
                            flexDirection="row"
                            align="center"
                            justify="center"
                            gap="2"
                          >
                            <Link
                              to={{
                                pathname: `/settings/block-times/${block?.id}`,
                                state: block
                              }}
                            >
                              <Tooltip
                                label={t('edit')}
                                aria-label={t('edit')}
                                hasArrow
                                placement="top-start"
                              >
                                <Box ml="1rem" w="24px">
                                  <FiEdit
                                    color="#9699B0"
                                    size={16}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </Box>
                              </Tooltip>
                            </Link>
                            <Tooltip
                              label={t('exclude')}
                              aria-label={t('exclude')}
                              hasArrow
                              placement="top-start"
                              onClick={() => {
                                setIdExclude(block.id)
                                onOpen()
                              }}
                            >
                              <Box ml="1rem" w="24px">
                                <AiFillDelete
                                  color="#9699B0"
                                  size={16}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setIdExclude(block.id)
                                    onOpen()
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          </Flex>
                        </Td>
                      </Tr>
                    )
                  )}
                </>
              )}
            </Tbody>
          </Table>
          {getBlockTimesData?.totalCountOfPage === 0 && !isLoading && (
            <NotFound />
          )}
        </Box>
        {!(getBlockTimesData?.totalCountOfPage === 0 && !isLoading) && (
          <Pagination
            totalCountOfRegisters={getBlockTimesData?.totalCountOfPage ?? 0}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
      </LayoutList>
      <ModalConfirmExclude
        isOpen={isOpen}
        title="Confirma Exclusão:"
        onConfirmExclude={async () => {
          await handleExcludedBlockById(idExclude)
          onClose()
        }}
        onClose={onClose}
      >
        <p>Tem certeza que quer excluir este bloqueio?</p>
      </ModalConfirmExclude>
    </>
  )
}
